export interface SampleProvider {
  label: string;
  latest_version: string;
}

export interface SampleWebhook {
  headers: Record<string, string>;
  body: unknown;
}

// Led with regardless of measured volume. These are the webhook senders a
// first-time visitor is most likely to recognise, so they anchor the list even
// where traffic alone would rank them differently.
const FEATURED_PROVIDERS = ['stripe', 'shopify', 'github'];

// The rest, ordered by the inbound webhook traffic Hookdeck receives from each,
// most first, measured by the user-agent on incoming requests over a trailing
// 90 days (August 2026).
//
// Several high-volume senders are absent because no samples exist for them
// yet — Meta (207M requests), AfterShip (92M), Svix (51M), Attio (40M),
// Ibanity (38M), Amazon (35M) and Google (32M). Add them here once they have
// samples. Paddle Classic is left out deliberately: the traffic data has a
// single `Paddle` user-agent that can't distinguish it from Paddle Billing,
// and Classic is the legacy product.
//
// Customer.io ranks high on traffic but is deliberately not promoted: that
// volume is likely dominated by Hookdeck's own usage, so it measures us rather
// than general popularity. It sorts alphabetically with everything else.
const PROVIDERS_BY_TRAFFIC = ['sendgrid', 'paddlebilling', 'chargebee', 'telnyx', 'hubspot'];

// Widely recognised providers we don't currently see meaningful traffic from.
// Surfaced above the alphabetical tail because someone opening Example
// Webhooks is more likely to be looking for these than for the providers they
// happen to sit next to. Ordered alphabetically — unlike the list above there
// is no measurement to rank them by, so any order would be arbitrary.
const NOTABLE_PROVIDERS = [
  'adyen',
  'bigcommerce',
  'calendly',
  'discord',
  'gitlab',
  'intercom',
  'mailchimp',
  'openai',
  'paypal',
  'slack',
  'square',
  'woocommerce',
  'zendesk',
];
const POPULAR_PROVIDERS = [...FEATURED_PROVIDERS, ...PROVIDERS_BY_TRAFFIC, ...NOTABLE_PROVIDERS];

function popularityRank(key: string) {
  const index = POPULAR_PROVIDERS.indexOf(key);
  return index === -1 ? POPULAR_PROVIDERS.length : index;
}

export function compareProviders([a]: [string, unknown], [b]: [string, unknown]) {
  return popularityRank(a) - popularityRank(b) || a.localeCompare(b);
}

async function fetchSamples(base_url: string, path: string, signal?: AbortSignal) {
  const response = await fetch(`${base_url.replace(/\/$/, '')}${path}`, {
    signal,
    credentials: 'omit',
  });
  if (!response.ok) throw new Error('Could not load the webhook library. Please try again.');
  return response.json();
}

export async function getSampleProviders(base_url: string, signal?: AbortSignal) {
  const providers = (await fetchSamples(base_url, '/providers.json', signal)) as Record<
    string,
    SampleProvider
  >;
  return Object.fromEntries(Object.entries(providers).sort(compareProviders));
}

export async function getSampleWebhooks(
  base_url: string,
  provider: string,
  version: string,
  signal?: AbortSignal,
): Promise<Record<string, SampleWebhook>> {
  return fetchSamples(
    base_url,
    `/providers/${encodeURIComponent(provider)}/${encodeURIComponent(version)}.json`,
    signal,
  );
}
